import Image from "next/image"
import formatNumber from "../../util/formatNumber"

interface IPetCostBreakdownProps {
  costOfLiving: "low" | "medium" | "high"
  metroName: string
}

export default function PetCostBreakdown({
  costOfLiving,
  metroName,
}: IPetCostBreakdownProps) {
  const costMultiplier =
    costOfLiving === "low" ? 0.6 : costOfLiving === "medium" ? 0.8 : 1

  return (
    <section className="relative w-full page-padding flex flex-col md:flex-row gap-x-8 lg:gap-x-16 xl:gap-x-24">
      <Image
        className="md:w-1/3 xl:w-1/2 object-contain"
        src="/images/woman-dog-polaroid.webp"
        alt="woman and dog"
        width={303}
        height={425}
      />
      <article className="space-y-4">
        <h2 className="font-montserrat font-semibold text-3xl">
          How expensive is it to own and care for a pet in {metroName}?
        </h2>
        <p className="text-lg text-gray7">
          {costOfLiving === "low" &&
            `Good news! The ${metroName} area is not only great for your pup, but it’s easy on your wallet. The cost of living in ${metroName} is below the national average, making pet ownership within reach for most people. Here’s what you can expect to spend on common pet expenses.`}
          {costOfLiving === "medium" &&
            `The ${metroName} area is a great place to raise your pup, but it can be challenging on your wallet. The cost of living in ${metroName} is around the national average, making pet ownership something you’ll want to budget ahead of time. Here’s what you can expect to spend on common pet expenses. `}
          {costOfLiving === "high" &&
            `${metroName} is a great city for any pup, but it might be tough on your wallet. Living in ${metroName} costs more than twice the US average, making pet ownership a difficult reality. Here's what you can expect to spend on common pet expenses.`}
        </p>
        <h5 className="text-xl font-semibold font-montserrat">
          First year of pet ownership
        </h5>
        <ul className="pl-0 space-y-4 text-gray7">
          <li>
            <strong className="font-semibold text-gray8 mr-2.5">
              ${formatNumber((100 * costMultiplier).toFixed(0))} - $
              {formatNumber((2000 * costMultiplier).toFixed(0))}
            </strong>
            Purchase/acquire pet
          </li>
          <li>
            <strong className="font-semibold text-gray8 mr-2.5">
              ${formatNumber((100 * costMultiplier).toFixed(0))} - $
              {formatNumber((300 * costMultiplier).toFixed(0))}
            </strong>
            Average of first year vet bills
          </li>
          <li>
            <strong className="font-semibold text-gray8 mr-2.5">
              ${formatNumber((37 * costMultiplier).toFixed(0))} - $
              {formatNumber((210 * costMultiplier).toFixed(0))}
            </strong>
            Licensing/registrations
          </li>
          <li>
            <strong className="font-semibold text-gray8 mr-2.5">
              ${formatNumber((125 * costMultiplier).toFixed(0))} - $
              {formatNumber((700 * costMultiplier).toFixed(0))}
            </strong>
            Average food costs (dependent on size of dog/brand of food)
          </li>
          <li>
            <strong className="font-semibold text-gray8 mr-2.5">
              ${formatNumber((20 * costMultiplier).toFixed(0))} - $
              {formatNumber((50 * costMultiplier).toFixed(0))}
            </strong>
            Dog walking supplies
          </li>
          <li>
            <strong className="font-semibold text-gray8 mr-2.5">
              ${formatNumber((25 * costMultiplier).toFixed(0))} - $
              {formatNumber((150 * costMultiplier).toFixed(0))}
            </strong>
            Toys and treats
          </li>
          <li>
            <strong className="font-semibold text-gray8 mr-2.5">
              ${formatNumber((650 * costMultiplier).toFixed(0))} - $
              {formatNumber((750 * costMultiplier).toFixed(0))}
            </strong>
            Grooming (once a month)
          </li>
          <li>
            <strong className="text-blue7 text-xl font-bold mr-2.5">
              ${formatNumber((1057 * costMultiplier).toFixed(0))} - $
              {formatNumber((4160 * costMultiplier).toFixed(0))}
            </strong>
            Total yearly expenses
          </li>
        </ul>
      </article>
    </section>
  )
}
